import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@jotpsych/ui';
import { initMswDevMode } from './mocks/msw-dev';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { setAppRouter } from './routes/routerRef';
import { Provider as ReduxProvider } from 'react-redux';
import './index.css';
import { UIStateProvider } from './context/uiState';
import { SelectedUserProvider } from './context/selectedUserContext.tsx';
import { BrandLanguageProvider } from '@context/brand-language/context';
import { DeleteModalProvider } from './context/modals/DeleteModalContext';
import SentryService from './services/SentryService';
import { SnackbarProvider } from 'notistack';
import { Zoom, Alert } from '@mui/material';
import { Snackbar } from './components/ui/feedback/Snackbar';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GlobalTutorialProvider } from './components/Tutorial/GlobalTutorialContext';
import { getRouter } from './routes';
import { store } from './store';
import { DebugProvider } from './context/DebugContext';
import { TrackingProvider } from './context/TrackingContext';
import { DelayedPersistGate } from './components/guards/DelayedPersistGate';

import { SentryErrorBoundary } from './services/SentryService';
import { ServerErrorPageStandalone } from './components/errors/ServerErrorPageStandalone';
import * as Sentry from '@sentry/react';
import { StatsigProviderWrapper } from './services/statsig';
import { UTMTracker } from './services/utm';
import { VersionChecker } from './components/VersionChecker';
import { BUILD_ENV } from './config/appVersion';
import { captureHostSwitchArrival } from './features/user/utils/hostSwitch';

// Initialize UTM tracking early to capture parameters
UTMTracker.capture();

// Record (and strip) the host-switch marker before the router can rewrite the
// URL, so the user-info effect can tell a bounce-back from a first switch.
captureHostSwitchArrival();

// Create the router with our routes and the appropriate basename
const router = createBrowserRouter(getRouter(), {
  basename: import.meta.env.VITE_ROUTER_BASENAME,
});
// Expose the instance for out-of-tree infrastructure (see routes/routerRef.ts).
setAppRouter(router);

// Toggle for authentication banner
const SHOW_AUTH_BANNER = false;

// SentryService skips initialization in local development and automated tests.
SentryService.initialize({
  dsn: 'https://8cf568e22a82ba04ed2b260af2025617@o4506123418206208.ingest.us.sentry.io/4507573931016192',
  environment: BUILD_ENV,
});

const FallbackComponent: Sentry.FallbackRender = errorData => {
  const { error } = errorData;
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
  const errorStack = error instanceof Error ? error.stack : 'No stack trace available.';

  return <ServerErrorPageStandalone message={errorMessage} stack={errorStack} />;
};

// Bootstrap the application (async to allow MSW initialization in dev mode)
async function bootstrap() {
  // Initialize MSW for development mode (if enabled via VITE_MSW_DEV_MODE=true)
  await initMswDevMode();

  // Create the root component with all providers
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <ReduxProvider store={store}>
      <DelayedPersistGate>
        <StatsigProviderWrapper>
          <DebugProvider>
            <UIStateProvider>
              <ThemeProvider>
                <BrandLanguageProvider>
                  <GlobalTutorialProvider>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <SnackbarProvider
                        autoHideDuration={3000}
                        TransitionComponent={Zoom}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        Components={{
                          default: Snackbar,
                          success: Snackbar,
                          error: Snackbar,
                          warning: Snackbar,
                          info: Snackbar,
                        }}
                      >
                        <DeleteModalProvider>
                          <SelectedUserProvider>
                            <TrackingProvider>
                              <SentryErrorBoundary fallback={FallbackComponent}>
                                {SHOW_AUTH_BANNER && (
                                  <Alert
                                    severity="error"
                                    sx={{
                                      borderRadius: 0,
                                      position: 'fixed',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      zIndex: 1400,
                                      width: '100%',
                                    }}
                                  >
                                    We're currently experiencing a temporary login outage. Our team is
                                    actively working on a resolution and wants to assure you that no
                                    notes or patient data are effected. You will regain access to all
                                    your notes as soon as we've resolved the issue.
                                    <br />
                                    We sincerely apologize for any inconvenience this may cause and
                                    understand how frustrating it can be. Your patience and
                                    understanding mean a lot to us, and we're committed to keeping you
                                    updated.
                                    <br />
                                    Please check back shortly for more information.
                                  </Alert>
                                )}
                                <VersionChecker />
                                <RouterProvider router={router} />
                              </SentryErrorBoundary>
                            </TrackingProvider>
                          </SelectedUserProvider>
                        </DeleteModalProvider>
                      </SnackbarProvider>
                    </LocalizationProvider>
                  </GlobalTutorialProvider>
                </BrandLanguageProvider>
              </ThemeProvider>
            </UIStateProvider>
          </DebugProvider>
        </StatsigProviderWrapper>
      </DelayedPersistGate>
    </ReduxProvider>,
  );
}

bootstrap();
